'use client'

import { useCallback, useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { toast } from 'sonner'
import { ArrowLeft, Calculator, Download, FileText, Wallet } from 'lucide-react'
import type { MonthlyReport } from '@/types/monthly'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { exportStipendReportDoc, type StipendExportEmployee } from '@/lib/export-stipend-report'
import { exportPayslip } from '@/lib/export-payslip'

export interface SavedStipendCalculation {
  id: string
  userId: string
  userName: string
  month: string
  startDate: string
  endDate: string
  allocatedSalary: number
  leavesTaken: number
  paidLeaveDays: number
  unpaidLeaveDays: number
  reductions: number
  totalDays: number
  workedDays: number
  dailyRate: number
  payableDays: number
  baseEarned: number
  totalPay: number
  savedByName: string | null
  savedAt: string
}

interface EmployeeStipendReportsProps {
  employee: StipendExportEmployee & { id: string; name: string }
  onBack: () => void
}

function rupee(value: number | null | undefined) {
  return `₹${Math.round(value || 0).toLocaleString('en-IN')}`
}

function monthLabel(month: string) {
  const [year, monthNumber] = String(month || '').split('-')
  const date = new Date(Number(year), Number(monthNumber) - 1, 1)
  return Number.isNaN(date.getTime()) ? month : date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
}

function formatDateTime(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso || '—'
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

export function EmployeeStipendReports({ employee, onBack }: EmployeeStipendReportsProps) {
  const [reports, setReports] = useState<MonthlyReport[]>([])
  const [calculations, setCalculations] = useState<SavedStipendCalculation[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // Settled independently so a missing saved-calculation history doesn't
      // blank the generated reports (and vice versa).
      const asJson = async (p: Promise<Response>) => (await p).json()
      const [reportsSettled, calcsSettled] = await Promise.allSettled([
        asJson(apiFetch(`/api/performance/monthly?userId=${employee.id}`)),
        asJson(apiFetch(`/api/performance/stipend-calculations?userId=${employee.id}`)),
      ])

      if (reportsSettled.status === 'fulfilled') setReports(reportsSettled.value.reports || [])
      else toast.error(reportsSettled.reason?.message || 'Failed to load stipend reports')

      if (calcsSettled.status === 'fulfilled') setCalculations(calcsSettled.value.calculations || [])
      else console.error('Failed to load saved calculations:', calcsSettled.reason)
    } finally {
      setLoading(false)
    }
  }, [employee.id])

  useEffect(() => {
    load()
  }, [load])

  const finalized = reports.filter(r => r.finalStipend != null)
  const totalPaid = finalized.reduce((sum, r) => sum + (r.finalStipend || 0), 0)

  return (
    <PageShell>
      <SectionHeader
        icon={Wallet}
        title={`${employee.name} — Stipend history`}
        description={[employee.employeeId, employee.role, employee.assignedHrName ? `HR: ${employee.assignedHrName}` : null]
          .filter(Boolean)
          .join(' · ') || 'All saved stipend reports and calculations'}
        action={
          <Button variant="outline" onClick={onBack} className="border-neutral-200 dark:border-white/10">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to stipends
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <KpiTile label="Monthly reports" value={reports.length} icon={FileText} tone="orange" />
        <KpiTile label="Finalized" value={finalized.length} icon={Wallet} tone="green" />
        <KpiTile label="Saved calculations" value={calculations.length} icon={Calculator} tone="cyan" />
        <KpiTile label="Total finalized" value={rupee(totalPaid)} icon={Wallet} tone="violet" />
      </div>

      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle>Generated monthly reports</GlassCardTitle>
          <span className="text-xs text-neutral-400 dark:text-white/40">{reports.length} month{reports.length === 1 ? '' : 's'}</span>
        </GlassCardHeader>
        {loading ? (
          <TabLoader />
        ) : reports.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No stipend reports yet"
            description="Generate a monthly stipend report to build this history."
          />
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-200 dark:border-white/10 hover:bg-transparent">
                    <TableHead className="text-neutral-500 dark:text-white/50">Month</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Status</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Payable hours</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Tasks</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Suggested</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50">Final</TableHead>
                    <TableHead className="text-neutral-500 dark:text-white/50 text-right">Export</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map(report => (
                    <TableRow key={report.id} className="border-neutral-100 dark:border-white/5 hover:bg-neutral-50 dark:hover:bg-white/5">
                      <TableCell className="text-neutral-900 dark:text-white/90 font-medium">{monthLabel(report.month)}</TableCell>
                      <TableCell>
                        {report.finalStipend != null
                          ? <StatusPill tone="green" dot>Finalized</StatusPill>
                          : <StatusPill tone="amber" dot>Review</StatusPill>}
                      </TableCell>
                      <TableCell className="text-neutral-800 dark:text-white/80">
                        {report.hours?.payable ?? report.hours?.total ?? 0}h / {report.hours?.expected ?? 0}h
                      </TableCell>
                      <TableCell className="text-neutral-800 dark:text-white/80">{report.tasks?.completed ?? 0}</TableCell>
                      <TableCell className="text-neutral-800 dark:text-white/80">{rupee(report.suggestedStipend)}</TableCell>
                      <TableCell className="text-neutral-900 dark:text-white/90 font-medium">
                        {report.finalStipend != null ? rupee(report.finalStipend) : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Export payslip (print / PDF)"
                            onClick={() => {
                              try {
                                exportPayslip(report, employee)
                              } catch (e: any) {
                                toast.error(e.message || 'Could not open the payslip window')
                              }
                            }}
                            className="text-orange-600 dark:text-orange-300"
                          >
                            <Download className="h-4 w-4 mr-1" /> Payslip
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Download as Word document"
                            onClick={() => exportStipendReportDoc(report, employee)}
                            className="text-blue-500 dark:text-blue-400"
                          >
                            <FileText className="h-4 w-4 mr-1" /> Word
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </GlassCard>

      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle>Saved salary calculations</GlassCardTitle>
          <span className="text-xs text-neutral-400 dark:text-white/40">{calculations.length} saved</span>
        </GlassCardHeader>
        {loading ? (
          <TabLoader />
        ) : calculations.length === 0 ? (
          <EmptyState
            icon={Calculator}
            title="No saved calculations"
            description="Run the HR salary calculator for this person and press Save to record it here."
          />
        ) : (
          <div className="space-y-3">
            {calculations.map(calc => (
              <div
                key={calc.id}
                className="rounded-xl p-3.5 bg-neutral-50/50 dark:bg-white/[0.02] ring-1 ring-inset ring-neutral-200 dark:ring-white/5"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white/90">
                      {calc.startDate} → {calc.endDate}
                    </p>
                    <p className="text-[11px] text-neutral-400 dark:text-white/40">
                      Saved {formatDateTime(calc.savedAt)}{calc.savedByName ? ` by ${calc.savedByName}` : ''}
                    </p>
                  </div>
                  <p className="text-lg font-semibold tabular-nums text-orange-600 dark:text-orange-400">{rupee(calc.totalPay)}</p>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <Cell label="Allocated" value={rupee(calc.allocatedSalary)} />
                  <Cell label="Days worked" value={`${calc.workedDays}/${calc.totalDays}`} />
                  <Cell label="Leave (paid/unpaid)" value={`${calc.paidLeaveDays}/${calc.unpaidLeaveDays}`} />
                  <Cell label="Reductions" value={rupee(calc.reductions)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </PageShell>
  )
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-neutral-400 dark:text-white/40">{label}</p>
      <p className="text-neutral-800 dark:text-white/80 font-medium">{value}</p>
    </div>
  )
}
